import type { AxiosInstance } from 'axios';

import type { AuthService } from 'types/AuthService';

import type { ConfigUseCases } from '@onboarding/composition/types';
import { createOnboardingHttpClient } from '@onboarding/infrastructure/api';
import { createOnboardingApiMapper } from '@onboarding/infrastructure/mappers';
import {
  createTableServiceRepository,
  createCashRegisterRepository,
  createTipRepository,
  createTaxRepository,
  createStoreAdminRepository,
  createReceiptRepository,
  createHubspotRepository,
} from '@onboarding/infrastructure/repositories';
import {
  applyTableServiceUseCase,
  fetchExistingAreaNamesUseCase,
  applyCashRegisterUseCase,
  applyTipUseCase,
  applyTaxUseCase,
  fetchStoreAdminInfoUseCase,
  saveReceiptHeaderUseCase,
  fetchStoreBrandsUseCase,
  updateHubspotPropertiesUseCase,
} from '@onboarding/application/use-cases';

/**
 * Assembles all step configuration use cases with their dependencies.
 * Called ONCE during composition initialization.
 */
export const createConfigUseCases = (
  httpClient: AxiosInstance,
  payHttpClient: AxiosInstance,
  adminHttpClient: AxiosInstance,
  authService: AuthService,
): ConfigUseCases => {
  const onboardingHttpClient = createOnboardingHttpClient({ httpClient, payHttpClient, adminHttpClient, authService });
  const mapper = createOnboardingApiMapper();
  const tableServicePort = createTableServiceRepository({ httpClient: onboardingHttpClient, mapper });
  const cashRegisterPort = createCashRegisterRepository({ httpClient: onboardingHttpClient });
  const tipPort = createTipRepository({ httpClient: onboardingHttpClient });
  const taxPort = createTaxRepository({ httpClient: onboardingHttpClient });
  const storeAdminPort = createStoreAdminRepository({ httpClient: onboardingHttpClient });
  const receiptPort = createReceiptRepository({ httpClient: onboardingHttpClient });
  const hubspotPort = createHubspotRepository({ httpClient: onboardingHttpClient });

  return {
    applyTableService: applyTableServiceUseCase({ tableServicePort }),
    fetchExistingAreaNames: fetchExistingAreaNamesUseCase({ tableServicePort }),
    applyCashRegister: applyCashRegisterUseCase({ cashRegisterPort }),
    applyTip: applyTipUseCase({ tipPort }),
    applyTax: applyTaxUseCase({ taxPort }),
    fetchStoreAdminInfo: fetchStoreAdminInfoUseCase({ storeAdminPort }),
    saveReceiptHeader: saveReceiptHeaderUseCase({ receiptPort }),
    fetchStoreBrands: fetchStoreBrandsUseCase({ receiptPort }),
    updateHubspotProperties: updateHubspotPropertiesUseCase({ hubspotPort }),
  };
};
